import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  archiveDocument,
  deleteCompanyDocument,
  listCompanyDocuments,
  unarchiveDocument,
  updateCompanyDocumentExpiry,
  uploadCompanyDocuments,
  listCompanyDocumentCategories,
  listManagedDocumentCategories,
  createManagedDocumentCategory,
  renameManagedDocumentCategory,
  deleteManagedDocumentCategory,
  type DocumentCategory,
  type DocumentOwnerType,
  type ListCompanyDocumentsParams,
  type UploadCompanyDocumentsParams,
} from '@/api/services/documents.service'
import { getErrorMessage } from '@/lib/getErrorMessage'

// ─── Query key factory ────────────────────────────────────────────────────────

export const documentQueryKeys = {
  all: ['documents'] as const,
  company: (companyId: string) => ['documents', 'company', companyId] as const,
  companyList: (
    companyId: string,
    ownerType: DocumentOwnerType,
    filters?: {
      userId?: string
      category?: DocumentCategory
      includeArchived?: boolean
      search?: string
    },
  ) => ['documents', 'company', companyId, ownerType, filters] as const,
}

// ─── Company document queries ─────────────────────────────────────────────────

export interface UseCompanyDocumentsOptions {
  ownerType: DocumentOwnerType
  userId?: string
  category?: DocumentCategory
  documentGroupId?: string
  includeArchived?: boolean
  search?: string
  enabled?: boolean
}

export function useCompanyDocuments(
  companyId: string | null,
  options: UseCompanyDocumentsOptions,
) {
  const { ownerType, userId, category, documentGroupId, includeArchived, search, enabled = true } = options
  const params: ListCompanyDocumentsParams = {
    ownerType,
    ...(userId ? { userId } : {}),
    ...(category ? { category } : {}),
    ...(documentGroupId ? { documentGroupId } : {}),
    ...(includeArchived !== undefined ? { includeArchived } : {}),
    ...(search ? { search } : {}),
  }

  return useQuery({
    queryKey: documentQueryKeys.companyList(companyId ?? '', ownerType, {
      userId,
      category,
      includeArchived,
      search,
    }),
    queryFn: () => listCompanyDocuments(companyId!, params),
    enabled: !!companyId && enabled,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCompanyDocumentCategories(
  companyId: string | null,
  ownerType: 'company' | 'staff',
  enabled = true,
) {
  return useQuery({
    queryKey: [...documentQueryKeys.company(companyId ?? ''), 'categories', ownerType],
    queryFn: () => listCompanyDocumentCategories(companyId!, ownerType),
    enabled: !!companyId && enabled,
    staleTime: 1 * 60 * 1000,
  })
}

const managedCategoryKey = ['documents', 'category-management'] as const

export function useManagedDocumentCategories() {
  return useQuery({ queryKey: managedCategoryKey, queryFn: listManagedDocumentCategories })
}

export function useCreateManagedDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ownerType, name }: { ownerType: 'company' | 'staff'; name: string }) =>
      createManagedDocumentCategory(ownerType, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Category added')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useRenameDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      renameManagedDocumentCategory(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Category renamed')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

export function useDeleteDocumentCategory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteManagedDocumentCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: managedCategoryKey })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Category deleted')
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })
}

// ─── Upload mutations ─────────────────────────────────────────────────────────

export interface UploadCompanyDocumentsMutationArgs {
  files: File[]
  params: UploadCompanyDocumentsParams
}

export function useUploadCompanyDocuments(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ files, params }: UploadCompanyDocumentsMutationArgs) =>
      uploadCompanyDocuments(companyId!, files, params),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      }
      // User-owned docs also surface in the per-user lists and count badges.
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Documents uploaded')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

// ─── Archive / unarchive mutations ───────────────────────────────────────────

export function useArchiveDocument(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, archiveReason }: { id: string; archiveReason?: string }) =>
      archiveDocument(companyId!, id, archiveReason),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      }
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Document archived')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUnarchiveDocument(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => unarchiveDocument(companyId!, id),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      }
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Document unarchived')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

// ─── Expiry mutation ──────────────────────────────────────────────────────────

export function useUpdateCompanyDocumentExpiry(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      expiryDate,
      documentType,
    }: {
      id: string
      expiryDate: string | null
      documentType?: string | null
    }) => updateCompanyDocumentExpiry(companyId!, id, expiryDate, documentType),
    onSuccess: (_data, { documentType }) => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      }
      toast.success(documentType === undefined ? 'Expiry date updated' : 'Document details updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

// ─── Delete mutation ──────────────────────────────────────────────────────────

export function useDeleteCompanyDocument(companyId: string | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteCompanyDocument(companyId!, id),
    onSuccess: () => {
      if (companyId) {
        queryClient.invalidateQueries({ queryKey: documentQueryKeys.company(companyId) })
      }
      queryClient.invalidateQueries({ queryKey: ['users', 'documents'] })
      toast.success('Document deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
